import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import ServerPaginatedTable from './ServerPaginatedTable';
import { useSelector, useDispatch } from 'react-redux';
import PageHeader from './PageHeader';
import TransactionModalAdmin from './TransactionModalAdmin';
import moment from 'moment-timezone';
import MobileHeader from './MobileHeader';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

const TransactionTableAdmin = () => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const primaryColor = theme.primary;
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-based state with defaults
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 50;
  const sortField = searchParams.get('sortField') || 'TransactionDate';
  const sortDirection = searchParams.get('sortDirection') || 'DESC';
  const searchQuery = searchParams.get('search') || '';
  const policyFilter = searchParams.get('policyFilter') || '';
  const urlStartDate = searchParams.get('startDate');
  const urlEndDate = searchParams.get('endDate');

  // Date state with URL persistence
  const [startDate, setStartDate] = useState(
    urlStartDate ? new Date(urlStartDate) : new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(
    urlEndDate ? new Date(urlEndDate) : new Date()
  );

  // Local state
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [aggregates, setAggregates] = useState({
    totalTransactions: 0,
    totalUserAmount: 0,
    totalCost: 0,
    totalProfit: 0
  });
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransactionDetails, setSelectedTransactionDetails] = useState(null);
  const policies = useSelector((state) => state.policies.activePolicies);

  // Helper function to update URL parameters
  const updateSearchParams = useCallback((updates) => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value.toString());
        }
      });

      // Remove default values to keep URL clean
      if (newParams.get('page') === '1') newParams.delete('page');
      if (newParams.get('pageSize') === '50') newParams.delete('pageSize');
      if (newParams.get('sortField') === 'TransactionDate') newParams.delete('sortField');
      if (newParams.get('sortDirection') === 'DESC') newParams.delete('sortDirection');

      return newParams;
    });
  }, [setSearchParams]);

  const filterConfig = [
    { key: 'TransactionId', type: 'number' },
    { key: 'policyNumber', type: 'number' },
    { key: 'locationName', type: 'string' },
    { key: 'locationCity', type: 'string' },
    { key: 'locationstate', type: 'string' },
    { key: 'AmountSum', type: 'number' },
    { key: 'UserAmountSum', type: 'number' },
    { key: 'Profit', type: 'number' },
    { key: 'TransactionDate', type: 'date' }
  ];



  const handleViewDetailsClick = async (TransactionId) => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getAdminTransactionDetails',
          payload: { userId: currentUser?.userId, TransactionId }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTransactionDetails(data);
        setIsModalOpen(true);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
      setLoading(false);

    } catch (error) {
      setAlertMessage(`Error fetching transaction details: ${error.message}`);
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransactionDetails(null);
  };

  const clearAlert = () => setAlertMessage(null);

  // Optimized fetch function using new backend
  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getAdminTransactionsPaginated',
          payload: {
            userId: currentUser?.userId,
            startDate: params.startDate || startDate.toISOString(),
            endDate: params.endDate || endDate.toISOString(),
            page: params.page || currentPage,
            pageSize: params.pageSize || pageSize,
            sortField: params.sortField || sortField,
            sortDirection: params.sortDirection || sortDirection,
            search: params.search !== undefined ? params.search : searchQuery,
            policyFilter: params.policyFilter !== undefined ? params.policyFilter : policyFilter,
            forceRefresh: params.forceRefresh || false
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setPagination(result.pagination);
        setAggregates(result.aggregates || {
          totalTransactions: 0,
          totalUserAmount: 0,
          totalCost: 0,
          totalProfit: 0
        });
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    currentUser?.userId,
    startDate,
    endDate,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    searchQuery,
    policyFilter
  ]);

  // Pagination and sorting handlers
  const handlePageChange = (newPage) => {
    updateSearchParams({ page: newPage });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateSearchParams({
      pageSize: newPageSize,
      page: 1 // Reset to page 1 when changing page size
    });
  };

  const handleSort = (field, direction) => {
    updateSearchParams({
      sortField: field,
      sortDirection: direction,
      page: 1 // Reset to page 1 when sorting
    });
  };

  // Date filter handlers - only submit when user clicks Apply Filters
  const handleFilterSubmit = () => {
    updateSearchParams({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      page: 1 // Reset to page 1 when filtering
    });
  };

  const handlePolicyFilterChange = (value) => {
    updateSearchParams({
      policyFilter: value,
      page: 1 // Reset to page 1 when filtering
    });
  };

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value ? new Date(value) : null);
    } else {
      setEndDate(value ? new Date(value) : null);
    }
    // Note: No automatic search - user must click Apply Filters
  };

  const [searchInput, setSearchInput] = useState(searchQuery);

  // Debounced search functionality
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateSearchParams({
          search: searchInput,
          page: 1 // Reset to page 1 when searching
        });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchQuery, updateSearchParams]);

  // Sync search input with URL parameter
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Fetch data when URL parameters change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Server-side filtering and pagination - no client-side filtering needed


  const columns = useMemo(() => [
    {
      name: "Date",
      selector: row => {
        const date = moment(row.TransactionDate).tz('America/Chicago');
        const formattedDate = date.format('MM/DD/YYYY');
        const formattedTime = date.format('hh:mm:ss A');
        return `${formattedDate} ${formattedTime}`;
      },
      sortable: true,
      sortField: 'TransactionDate',
      reorder: true,
    },
    {
      name: 'Policy',
      selector: row => row.policyNumber,
      sortable: true,
      sortField: 'policyNumber',
      reorder: true,
    },
    {
      name: 'Transaction ID',
      selector: row => row.TransactionId,
      sortable: true,
      sortField: 'TransactionId',
      reorder: true,
    },
    {
      name: 'Location',
      selector: row => row.locationName,
      sortable: true,
      sortField: 'locationName',
      reorder: true,
    },
    {
      name: 'City',
      selector: row => row.locationCity,
      sortable: true,
      sortField: 'locationCity',
      reorder: true,
    },
    {
      name: 'State',
      selector: row => row.locationstate,
      sortable: true,
      sortField: 'locationstate',
      reorder: true,
    },
    {
      name: 'Cost',
      selector: row => `$${parseFloat(row.AmountSum || 0).toFixed(2)}`,
      sortable: true,
      sortField: 'AmountSum',
      reorder: true,
    },
    {
      name: 'User Amount',
      selector: row => `$${parseFloat(row.UserAmountSum || 0).toFixed(2)}`,
      sortable: true,
      sortField: 'UserAmountSum',
      reorder: true,
    },
    {
      name: 'Profit',
      selector: row => row.Profit,
      cell: row => (
        <span style={{ color: row.Profit < 0 ? 'red' : 'black' }}>
          {`$${parseFloat(row.Profit || 0).toFixed(2)}`}
        </span>
      ),
      sortable: true,
      sortField: 'Profit',
      reorder: true,
    },
    {
      name: 'Details',
      cell: row => (
        <button
          className="themed-button"
          onClick={() => handleViewDetailsClick(row.TransactionId)}
        >
          View
        </button>
      ),
      reorder: true,
    },
  ], [handleViewDetailsClick]);


  return (
    <>
      <MobileHeader theme={theme} />
      <div className='mobile-container'>
        <PageHeader
          heading="Admin Transactions"
          context="Explore and review transaction details from the past 30 days. If you need information on transactions older than that, please utilize the reporting tool to generate the necessary reports."
        />
        <TransactionModalAdmin
          isOpen={isModalOpen}
          details={selectedTransactionDetails}
          onClose={handleCloseModal}
        />

        {/* Modern Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Transactions Card */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">Total Transactions</p>
                <p className="text-3xl font-bold text-blue-900">{aggregates.totalTransactions.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-500 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Revenue Card */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-green-900">${aggregates.totalUserAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div className="p-3 bg-green-500 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Cost Card */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 mb-1">Total Costs</p>
                <p className="text-3xl font-bold text-orange-900">${aggregates.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div className="p-3 bg-orange-500 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Profit Card */}
          <div className={`bg-gradient-to-br ${aggregates.totalProfit >= 0 ? 'from-emerald-50 to-emerald-100 border-emerald-200' : 'from-red-50 to-red-100 border-red-200'} border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${aggregates.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-600'} mb-1`}>Net Profit</p>
                <p className={`text-3xl font-bold ${aggregates.totalProfit >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                  ${aggregates.totalProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className={`p-3 ${aggregates.totalProfit >= 0 ? 'bg-emerald-500' : 'bg-red-500'} rounded-lg`}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {aggregates.totalProfit >= 0 ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                  )}
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Filter Section */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filter Transactions</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => fetchTransactions({ forceRefresh: true })}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                disabled={loading}
                title="Refresh data"
              >
                <FontAwesomeIcon
                  icon={faSync}
                  className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Search Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Search Transactions</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Transaction ID, Location, Card..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={startDate.toISOString().split('T')[0]}
                onChange={(e) => handleDateChange('start', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={endDate.toISOString().split('T')[0]}
                onChange={(e) => handleDateChange('end', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
              />
            </div>

            {/* Policy Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Policy Filter</label>
              <select
                value={policyFilter}
                onChange={(e) => handlePolicyFilterChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
              >
                <option value="">All Policies</option>
                {policies.map((policy) => (
                  <option key={policy.PolicyNumber} value={policy.PolicyNumber}>
                    {policy.PolicyNumber} - {policy.Description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Apply Filters Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleFilterSubmit}
              className="themed-button inline-flex items-center px-6 py-3 font-medium rounded-lg transition-colors duration-200 shadow-sm"
              disabled={loading}
              style={{ backgroundColor: primaryColor }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
              </svg>
              Apply Filters
            </button>
          </div>
        </div>
        <ServerPaginatedTable
          columns={columns}
          data={data}
          primaryColor={primaryColor}
          loading={loading}
          alertMessage={alertMessage}
          clearAlert={clearAlert}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          pageSizeOptions={[25, 50, 100]}
        />

      </div>
    </>
  );
};

export default TransactionTableAdmin;

